import { sentryLogger } from '../../../../monitor/monitor';
import { taskService } from '../../../../services';
import {
  calculateExpected,
  calculatePonderators,
  calculateProgress
} from '../../../../utils/lookahead-common';
import { TimerManagerSingleton } from '../../../../utils/timerManager';
import { findDeepGetActivityOfTask } from '../../../../views/lookahead/planification/index.helper';
import {
  findDeepGetTaskRecursive,
  updateActivityParent,
  updateAsyncActivityGantt,
  updateAsyncActivityHelperHack,
  updateAsyncTaskGanttV2
} from '../../GanttVisualization.helper';

const DECIMAL_PLACES = 2;
const PERCENTAGE = 100;
const LIMIT_PROGRESS = 99.99;
const expectedProgressCache = new Map();
const statusCache = new Map();

export const clearCaches = () => {
  expectedProgressCache.clear();
  statusCache.clear();
};

const formatDateString = (date) => {
  if (!date) return '';
  if (date.toISOString) return date.toISOString();
  return date.toString();
};

export const getExpectedProgress = (task, ganttAPI, calendarId) => {
  const uniqueId = task.id || JSON.stringify(task);
  const startString = formatDateString(task.start_date);
  const endString = formatDateString(task.end_date);

  if (expectedProgressCache.has(uniqueId)) {
    const cached = expectedProgressCache.get(uniqueId);
    if (
      cached.startString === startString &&
      cached.endString === endString &&
      cached.calendarId === calendarId
    ) {
      return cached.value;
    }
  }

  const computed = calculateExpected(task, ganttAPI, calendarId);
  expectedProgressCache.set(uniqueId, {
    startString,
    endString,
    calendarId,
    value: computed
  });
  return computed;
};

export const formatProgress = (value, lang) => {
  const decimals = Number.isInteger(value) ? 0 : DECIMAL_PLACES;
  const formatted = parseFloat(value).toFixed(decimals);
  return lang === 'en' ? formatted + '%' : formatted.replace('.', ',') + '%';
};

export const getTaskStatus = (task) => {
  const uniqueId = task.id || JSON.stringify(task);

  if (statusCache.has(uniqueId)) {
    const cached = statusCache.get(uniqueId);
    if (
      cached.progress === task.progress &&
      cached.expected === task.expected
    ) {
      return cached.status;
    }
  }

  const statusConditions = [
    { condition: () => task.progress > LIMIT_PROGRESS, status: 'Done' },
    { condition: () => task.expected > task.progress, status: 'Overdue' },
    { condition: () => task.progress > task.expected, status: 'Advancement' },
    {
      condition: () => task.progress === 0 && task.expected === 0,
      status: 'Waiting'
    },
    {
      condition: () => task.progress > 0 && task.progress < task.expected,
      status: 'Doing'
    },
    { condition: () => true, status: 'Waiting' }
  ];

  const status = statusConditions.find((item) => item.condition()).status;
  statusCache.set(uniqueId, {
    progress: task.progress,
    expected: task.expected,
    status
  });
  return status;
};

export const updateProgressStatus = (task) => {
  const DEFAULT_COLOR = '#52c41a';

  const statusConditions = [
    {
      condition: () => task.progress.toFixed(DECIMAL_PLACES) === '100.00',
      color: '#34AF00',
      flags: { Advancement: false, Overdue: false }
    },
    {
      condition: () => task.progress > task.expected,
      color: '#2C3421',
      flags: { Advancement: true, Overdue: false }
    },
    {
      condition: () => task.progress < task.expected,
      color: '#E50101',
      flags: { Advancement: false, Overdue: true }
    },
    {
      condition: () => true,
      color: DEFAULT_COLOR,
      flags: { Advancement: false, Overdue: false }
    }
  ];

  const result = statusConditions.find((item) => item.condition());

  Object.assign(task, result.flags);
  task.progressColor = result.color;

  return result.color;
};

export const findReferences = (task, activities) => {
  const activityReference = findDeepGetActivityOfTask(
    activities,
    'id',
    task.id
  );
  const taskFromLookahead = findDeepGetTaskRecursive(activities, 'id', task.id);
  const parentTask = taskFromLookahead?.parent_id
    ? findDeepGetTaskRecursive(activities, 'id', taskFromLookahead.parent_id)
    : null;

  return {
    activityReference,
    taskFromLookahead,
    parentTask
  };
};

export const calculateParentProgress = (parent) => {
  const childs = parent.tasks || parent.children;
  const progress = childs.reduce(
    (acc, child) => acc + (child.ponderator / PERCENTAGE) * child.progress,
    0
  );

  return progress;
};

export const getStatusFromProgress = (progress) => {
  if (progress === 0) return 'Waiting';
  if (progress < PERCENTAGE) return 'Doing';
  return 'Done';
};

export const getProjectState = () => {
  const currentSector = JSON.parse(sessionStorage.getItem('currentSector'));
  const currentProject = JSON.parse(sessionStorage.getItem('currentProject'));
  const projectState = {
    sectorSelected: currentSector.id,
    allProjects: [currentProject],
    projectSelected: currentProject.id
  };

  return projectState;
};

export const updateParentProgressRecursive = (taskId, activities, gantt) => {
  const parentTask = gantt.getTask(taskId);
  const referenceTask = findDeepGetTaskRecursive(activities, 'id', taskId);

  if (!referenceTask) return;

  referenceTask.progress = calculateParentProgress(referenceTask);

  if (gantt.isTaskExists(parentTask.id)) {
    parentTask.progress = referenceTask.progress;
    gantt.updateTask(parentTask.id);
  }

  if (referenceTask.parent_id) {
    updateParentProgressRecursive(referenceTask.parent_id, activities, gantt);
  }
};

export const updateTaskData = async (task) => {
  const gantt = window.ganttVisualization;
  const timerManager = TimerManagerSingleton.getInstance();
  const DELAY = 500;
  task.status = getStatusFromProgress(task.progress);

  await updateAsyncTaskGanttV2(
    task,
    ['progress', 'status'],
    null,
    gantt,
    true,
    false,
    false,
    true,
    true
  );

  const updateVisualProgress = () => {
    const ganttRef = gantt.getTask(task.id);
    if (ganttRef) {
      ganttRef.progress = task.progress;
      ganttRef.status = task.status;

      if (task.parent_id) {
        updateParentProgressRecursive(task.parent_id, window.activities, gantt);
      }
    }
  };

  timerManager.registerAutoTimeout(
    updateVisualProgress,
    DELAY,
    'renderProgressUpdateTimer'
  );
};

export const saveTaskProgress = (
  updateAsyncTask,
  task,
  newProgress,
  parent,
  activity,
  updateAsyncActivity
  // eslint-disable-next-line max-params
) => {
  task.progress = newProgress;

  if (newProgress === 0 && calculatePonderators) {
    const projectState = getProjectState();
    calculatePonderators(
      parent || activity,
      activity,
      updateAsyncTask,
      projectState
    );
  }

  if (updateAsyncTask) {
    updateAsyncTask(task);
  } else {
    taskService.update(task);
  }

  calculateProgress(
    task,
    parent,
    activity,
    updateAsyncTask,
    updateAsyncActivity
  );
};

export const updateTaskProgress = (task, progress) => {
  try {
    const activities = window.activities;
    const gantt = window.ganttVisualization;
    const { sectorSelected } = getProjectState();
    const { activityReference, taskFromLookahead, parentTask } = findReferences(
      task,
      activities
    );

    if (!activityReference || !taskFromLookahead) return;

    saveTaskProgress(
      updateTaskData,
      taskFromLookahead,
      parseFloat(progress),
      parentTask || activityReference,
      activityReference,
      updateAsyncActivityGantt(
        gantt,
        task,
        updateAsyncActivityHelperHack,
        updateActivityParent,
        sectorSelected
      )
    );
  } catch (error) {
    sentryLogger.error('Error updating task progress', {
      error,
      task,
      progress
    });
  }
};
